/**
 * The label a bill line prints under on a surface that does NOT show the GL
 * account separately: its typed description, else the GL account's NAME
 * ("General Admin Cost"). Lines entered with only a GL code — mid-month billing
 * of copies, postage, admin — carry no description at all (#577).
 *
 * Never the GL code number: on a customer- or vendor-facing document that is
 * internal chart structure. Surfaces that already render a GL column keep
 * showing the raw description, or the name would print twice.
 *
 * Returns undefined when the line has neither, so callers pick their own
 * placeholder (`—`) or drop the line from a joined list.
 */
export const billLineDescription = (
	line: { description?: string | null; glName?: string | null },
): string | undefined => line.description?.trim() || line.glName?.trim() || undefined;

/**
 * Headline + optional GL sub-line for a surface that DOES show the GL (the bill
 * detail page). With a description, the GL sits on its own line beneath it.
 * Without one, the GL label IS the headline and the sub-line goes away, rather
 * than a bare `—` over a grey GL line. `warn` carries through either way so a
 * missing/deleted GL code never loses its warning styling.
 */
export const billLineHeadline = (
	description: string | null | undefined,
	gl: { label: string; warn: boolean },
): { title: string; titleWarn: boolean; glLine: { label: string; warn: boolean } | null } => {
	const desc = description?.trim();
	if (desc) return { title: desc, titleWarn: false, glLine: gl.label ? gl : null };
	return { title: gl.label || '—', titleWarn: gl.warn, glLine: null };
};
